<script>
import EzButton from '@/components/ui/Button/EzButton';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch';
import EzCheckbox from '@/components/ui/Checkbox/Checkbox';

/**
 * VSelectSearchLocation
 * @version 1.0.0
 * @since 3.29.0
 */
export default {
  name: 'VSelectSearchLocation',
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    selectedData: {
      type: Array,
      required: true,
      default: () => [],
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    label: {
      type: String,
      default: '',
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    searchProperty: {
      type: String,
      default: '',
      required: false,
    },
    isFullWidth: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedId: {
      type: Number,
      required: false,
    },
    stopPropagation: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataCy: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Align dropdown to left.
     * Default value is right.
     */
    alignLeft: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    EzButton,
    VSelectSearch,
    EzCheckbox,
  },
  computed: {
    selectValue() {
      if (this.selectedData.length === 1) return this.selectedData[0].location.name;
      return 'Multiple';
    },
  },
  methods: {
    close() {
      this.$refs.chooseLocation.close();
    },
    addNew() {
      this.$emit('addLocation');
    },
    edit(item) {
      this.$emit('edit', item);
    },
    remove(item) {
      this.$emit('remove', item);
    },
    onSelect(item) {
      this.$emit('select', item);
    },
    reset() {
      this.$refs.chooseLocation.reset();
    },
    isChecked(result) {
      return !!this.selectedData.filter(item => item.location.id === result.id).length;
    },
  },
};
</script>

<template>
  <v-select-search
    ref="chooseLocation"
    :data="data"
    :name="name"
    :selected="selectedData.length ? selectedData[0] : selectedId"
    :label="label"
    :placeholder="placeholder"
    :searchProperty="searchProperty"
    isFullWidth
    :hasClear="false"
    scrollIntoView
    :stopPropagation="stopPropagation"
    :closeOnSelect="false"
    @selected="onSelect"
    :class="[
      'product-group',
      'product-group--first-action',
      { 'product-group--selected': selectedData.length },
    ]"
    :alignLeft="alignLeft"
    :disable="disabled"
  >
    <template #value
      ><span class="mr-4">{{ selectValue }}</span></template
    >
    <template #firstResult>
      <ez-button @click.stop="addNew" class="add-unit" type="blue-link">
        <font-awesome-icon icon="plus" /> Add New Location
      </ez-button>
    </template>
    <template #result="{ result }">
      <div class="result">
        <div class="result__item">
          <div class="result__item__name">
            <ez-checkbox class="cursor-pointer mr-8" :checked="isChecked(result)" />
            {{ result.name }}
          </div>

          <div class="result__item__actions">
            <ez-button type="link" @click.stop="edit(result)" class="action">
              <font-awesome-icon icon="pen" />
            </ez-button>
            <ez-button type="link" @click.stop="remove(result)" class="action">
              <font-awesome-icon icon="trash" />
            </ez-button>
          </div>
        </div>
      </div>
    </template>
  </v-select-search>
</template>

<style lang="scss" scoped>
.product-group {
  .add-unit {
    padding: 0;
    width: 100%;
    text-align: left;
  }

  :deep() {
    .select-search {
      width: auto;
      &__trigger {
        @include font-size(14px, 16px);

        span:not(.select-search__value) {
          color: $color-gray-6C;
        }
      }

      &__search-wrapper {
        padding: 8px 8px 0 8px;
        border: none;
        margin-bottom: 8px;

        .input-wrapper {
          @include font-size(14px, 16px);
          border: 1px solid $input-border-color;
          border-radius: $input-border-radius;
          padding: 0 10px;

          svg {
            color: $color-gray-6C;
          }
        }
      }

      &__list-wrapper {
        li {
          padding: 0;
        }
      }
    }

    label {
      color: $color-gray-6C;
    }
  }

  &--first-action {
    :deep() .select-search__list-wrapper {
      li {
        &:first-child {
          padding: 0 12px;
        }
      }
    }
  }

  .result {
    height: 36px;
    width: 100%;
    padding: 6px 12px;

    &__item {
      @include font-size(14px, 16px);
      height: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &__name {
        display: flex;
        align-items: center;
      }

      &__actions {
        @include font-size(14px);
        color: $color-gray-6C;

        .action {
          display: none;
          padding: 0;
          width: 36px;
          height: 36px;
        }
      }
    }

    &:hover {
      .action {
        display: initial;
      }
    }
  }
}
</style>
